import {
  AdminUserList,
  CreatedInvite,
  InviteList,
  OkResponse,
  PasskeyList,
  UserResponse,
  type AdminUser,
  type InviteSummary,
  type Passkey,
  type Role,
  type Units,
} from '@squats/shared';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { api, errorText } from '../api.ts';
import { useAuth, useUser } from '../auth.tsx';
import { CopyLink } from '../components/CopyLink.tsx';
import { StatusCard } from '../components/StatusCard.tsx';
import { usePendingCount } from '../components/SyncStatus.tsx';
import { formatDate, formatDateTime } from '../hooks.ts';
import { createPasskey, passkeyErrorMessage, signalRemainingPasskeys } from '../passkeys.ts';



export function SettingsPage() {
  const user = useUser();
  const { signOut } = useAuth();
  const pending = usePendingCount();
  return (
    <div className="stack-lg">
      <h1>Settings</h1>
      <ProfileSection />
      <PasskeysSection />
      {user.role === 'admin' && <AdminSection />}
      <ExportSection />
      <StatusCard />
      <section className="card stack">
        <h2>Sign out</h2>
        <p className="muted small">Signs out this browser only, and clears the workouts saved on it.</p>
        {pending > 0 && (
          <p className="notice">
            {pending} {pending === 1 ? 'change hasn’t' : 'changes haven’t'} uploaded yet. Signing out now deletes {pending === 1 ? 'it' : 'them'}; connect to the
            internet first to keep {pending === 1 ? 'it' : 'them'}.
          </p>
        )}
        <button type="button" className={`btn${pending ? ' danger' : ''}`} onClick={() => void signOut()}>
          {pending ? 'Sign out anyway' : 'Sign out'}
        </button>
      </section>
    </div>
  );
}

function ProfileSection() {
  const user = useUser();
  const { setUser } = useAuth();
  const [displayName, setDisplayName] = useState(user.displayName);
  const [units, setUnits] = useState<Units>(user.units);
  const [timezone, setTimezone] = useState(user.timezone);
  const [status, setStatus] = useState<{ kind: 'saved' } | { kind: 'error'; message: string } | null>(null);
  const zones = useMemo(() => {
    const all = Intl.supportedValuesOf('timeZone');
    return all.includes(user.timezone) ? all : [user.timezone, ...all];
  }, [user.timezone]);
  const dirty = displayName.trim() !== user.displayName || units !== user.units || timezone !== user.timezone;

  async function save(e: FormEvent) {
    e.preventDefault();
    try {
      const res = await api('/api/me', UserResponse, { method: 'PATCH', body: { displayName, units, timezone } });
      setUser(res.user);
      setDisplayName(res.user.displayName);
      setStatus({ kind: 'saved' });
    } catch (err) {
      setStatus({ kind: 'error', message: errorText(err) });
    }
  }

  return (
    <form className="card stack" onSubmit={save} aria-labelledby="profile-h">
      <h2 id="profile-h">Profile</h2>
      <label className="field">
        <span>Name</span>
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={50} required />
      </label>
      <fieldset className="field">
        <legend>Weight units</legend>
        <div className="segmented">
          {(['lb', 'kg'] as const).map((u) => (
            <label key={u}>
              <input type="radio" name="units" value={u} checked={units === u} onChange={() => setUnits(u)} />
              <span>{u}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <label className="field">
        <span>Time zone</span>
        <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
          {zones.map((z) => (
            <option key={z} value={z}>
              {z.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
      </label>
      <div className="row">
        <button type="submit" className="btn primary" disabled={!dirty}>
          Save
        </button>
        {status?.kind === 'saved' && !dirty && <span className="muted small">Saved</span>}
        {status?.kind === 'error' && <span className="error">{status.message}</span>}
      </div>
    </form>
  );
}

function PasskeysSection() {
  const user = useUser();
  const [passkeys, setPasskeys] = useState<Passkey[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    api('/api/me/passkeys', PasskeyList)
      .then((r) => setPasskeys(r.passkeys))
      .catch((e: unknown) => setError(errorText(e)));
  }, []);
  useEffect(reload, [reload]);

  async function add() {
    setBusy(true);
    setError(null);
    try {
      await createPasskey({});
      reload();
    } catch (e) {
      setError(passkeyErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove(p: Passkey) {
    if (!confirm(`Remove “${p.name}”? You won’t be able to sign in with it anymore.`)) return;
    setError(null);
    try {
      const res = await api(`/api/me/passkeys/${encodeURIComponent(p.id)}`, PasskeyList, { method: 'DELETE' });
      setPasskeys(res.passkeys);
      signalRemainingPasskeys(user.id, res.passkeys.map((k) => k.id));
    } catch (e) {
      setError(errorText(e));
    }
  }

  async function rename(p: Passkey, name: string) {
    try {
      const res = await api(`/api/me/passkeys/${encodeURIComponent(p.id)}`, PasskeyList, { method: 'PATCH', body: { name } });
      setPasskeys(res.passkeys);
    } catch (e) {
      setError(errorText(e));
    }
  }

  return (
    <section className="card stack" aria-labelledby="passkeys-h">
      <h2 id="passkeys-h">Passkeys</h2>
      {passkeys === null && !error && <p className="muted">Loading…</p>}
      {passkeys && (
        <ul className="list">
          {passkeys.map((p) => (
            <PasskeyRow key={p.id} passkey={p} onRename={(n) => rename(p, n)} onRemove={() => remove(p)} canRemove={passkeys.length > 1} />
          ))}
        </ul>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <div>
        <button type="button" className="btn" onClick={add} disabled={busy}>
          {busy ? 'Waiting for your passkey…' : 'Add a passkey'}
        </button>
      </div>
      <p className="muted small">
        Synced passkeys follow your iCloud or Google account to your other devices. Add another one to sign in from a
        phone that uses a different account (e.g. an Android and an iPhone).
      </p>
    </section>
  );
}

function PasskeyRow({
  passkey,
  onRename,
  onRemove,
  canRemove,
}: {
  passkey: Passkey;
  onRename: (name: string) => Promise<void>;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(passkey.name);

  async function submit(e: FormEvent) {
    e.preventDefault();
    await onRename(name);
    setEditing(false);
  }

  return (
    <li className="list-row">
      {editing ? (
        <form className="row grow" onSubmit={submit}>
          <input aria-label="Passkey name" value={name} onChange={(e) => setName(e.target.value)} maxLength={50} required autoFocus />
          <button type="submit" className="btn small primary">
            Save
          </button>
          <button type="button" className="btn small" onClick={() => setEditing(false)}>
            Cancel
          </button>
        </form>
      ) : (
        <>
          <div className="grow">
            <div className="row-title">
              {passkey.name} <span className="pill">{passkey.synced ? 'Synced' : 'This device only'}</span>
            </div>
            <div className="muted small">
              Added {formatDate(passkey.createdAt)} · {passkey.lastUsedAt ? `last used ${formatDateTime(passkey.lastUsedAt)}` : 'not used to sign in yet'}
            </div>
          </div>
          <div className="row">
            <button type="button" className="btn small" onClick={() => setEditing(true)}>
              Rename
            </button>
            {canRemove && (
              <button type="button" className="btn small danger" onClick={onRemove}>
                Remove
              </button>
            )}
          </div>
        </>
      )}
    </li>
  );
}

function AdminSection() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [signupsOpen, setSignupsOpen] = useState(false);
  const [invites, setInvites] = useState<InviteSummary[]>([]);
  const [created, setCreated] = useState<{ inviteId: string; label: string; url: string } | null>(null);
  const [role, setRole] = useState<Role>('member');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    Promise.all([api('/api/admin/users', AdminUserList), api('/api/admin/invites', InviteList)])
      .then(([u, i]) => {
        setUsers(u.users);
        setSignupsOpen(u.signupsOpen);
        setInvites(i.invites);
      })
      .catch((e: unknown) => setError(errorText(e)));
  }, []);
  useEffect(reload, [reload]);

  async function invite(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const res = await api('/api/admin/invites', CreatedInvite, { body: { role, note: note || undefined } });
      setCreated({ inviteId: res.invite.id, label: `Invite link${note ? ` for ${note}` : ''}`, url: res.url });
      setNote('');
      reload();
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function recovery(u: AdminUser) {
    setError(null);
    try {
      const res = await api(`/api/admin/users/${u.id}/recovery`, CreatedInvite, { body: {} });
      setCreated({ inviteId: res.invite.id, label: `Passkey recovery link for ${u.displayName}`, url: res.url });
      reload();
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function revoke(i: InviteSummary) {
    setError(null);
    try {
      await api(`/api/admin/invites/${i.id}`, OkResponse, { method: 'DELETE' });
      if (created?.inviteId === i.id) setCreated(null);
      reload();
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <section className="card stack" aria-labelledby="admin-h">
      <h2 id="admin-h">People</h2>
      <ul className="list">
        {users.map((u) => (
          <li key={u.id} className="list-row">
            <div className="grow">
              <div className="row-title">
                {u.displayName} {u.role === 'admin' && <span className="pill">Admin</span>}
              </div>
              <div className="muted small">
                {u.passkeyCount} passkey{u.passkeyCount === 1 ? '' : 's'} · joined {formatDate(u.createdAt)}
              </div>
            </div>
            <button type="button" className="btn small" onClick={() => void recovery(u)}>
              Recovery link
            </button>
          </li>
        ))}
      </ul>

      {!signupsOpen && (
        <p className="muted small">Squats is just the two of you, so it’s closed to new accounts. A recovery link adds a passkey to an existing account (for a new phone).</p>
      )}

      {signupsOpen && (
        <form className="stack" onSubmit={invite}>
          <h3>Invite someone</h3>
          <div className="row wrap">
            <label className="field grow">
              <span>Name or note (optional)</span>
              <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={50} placeholder="e.g. Sam" />
            </label>
            <label className="field">
              <span>Role</span>
              <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </label>
          </div>
          <div>
            <button type="submit" className="btn primary">
              Create invite link
            </button>
          </div>
        </form>
      )}

      {created && <CopyLink label={created.label} url={created.url} />}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {invites.length > 0 && (
        <>
          <h3>Open links</h3>
          <ul className="list">
            {invites.map((i) => (
              <li key={i.id} className="list-row">
                <div className="grow">
                  <div className="row-title">
                    {i.purpose === 'signup' ? `Invite${i.note ? ` for ${i.note}` : ''} (${i.role})` : `Recovery for ${i.forUser}`}
                  </div>
                  <div className="muted small">Expires {formatDateTime(i.expiresAt)}</div>
                </div>
                <button type="button" className="btn small danger" onClick={() => void revoke(i)}>
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/** Downloads of everything you've logged: one JSON with all of it, and CSVs that open in a spreadsheet. */
function ExportSection() {
  return (
    <section className="card stack" aria-labelledby="export-h">
      <h2 id="export-h">Your data</h2>
      <p className="muted small">Everything you’ve logged, yours to keep. Downloads include only your own data.</p>
      <div className="stack-sm">
        <a className="btn" href="/api/export/data.json" download>
          Everything (JSON)
        </a>
        <a className="btn" href="/api/export/sets.csv" download>
          Workout sets (CSV)
        </a>
        <a className="btn" href="/api/export/bodyweight.csv" download>
          Bodyweight (CSV)
        </a>
      </div>
    </section>
  );
}
